import type { TravelPlanDetails } from "../api/travelPlan";
import type { JourneyPlace, RouteLink } from "../types/FormTypes";

import dayjs from "dayjs";
import "dayjs/locale/sv";

dayjs.locale("sv");

const inferLineType = (link: any): string => {
  const group = (
    link?.RouteLinkGroupType ??
    link?.Line?.Ogt?.OgtTrafficTypeGroupKey ??
    ""
  ).toUpperCase();

  switch (group) {
    case "GRPGÅNG":
      return "GÅNG";

    case "GRPTÅG":
      return "TÅG";

    case "GRPBÅT":
      return "BÅT";

    case "GRPTÄTO":
      return "TÄTO";

    case "GRPLAND":
      return "LAND";

    case "GRPEXPRESS":
      return "EXPRESS";

    case "GRPNÄRTRAFIK":
      return "NÄRTRAFIK";

    default: {
      const lineName = link?.LineName ?? link?.Line?.LineName ?? "";

      const lineNumber = link?.LineNrReal ?? link?.Line?.LineNrReal;

      if (lineName.toLowerCase().includes("gång")) {
        return "GÅNG";
      }

      if (/^\d+$/.test(String(lineNumber ?? ""))) {
        return "BUSS";
      }

      return "TÅG";
    }
  }
};

const getOgtTrafficData = (
  type: string,
  lineName: string,
  groupKey: string
) => {
  const isBus = ["BUSS", "LAND", "TÄTO", "EXPRESS", "NÄRTRAFIK"].includes(type);

  const iconCss =
    type === "TÅG"
      ? "ogt-service-train"
      : type === "GÅNG"
      ? "ogt-service-walk"
      : type === "BÅT"
      ? "ogt-service-boat"
      : isBus
      ? "ogt-service-bus"
      : "ogt-service-unknown";

  return {
    OgtLineNamePreformatted:
      isBus && /^\d+$/.test(lineName) ? `Linje ${lineName}` : lineName,

    OgtTrafficTypeGroupKey: groupKey,
    OgtTrafficTypeGroupCssClass: type,
    OgtTrafficTypeIconCssClass: iconCss,

    OgtTrafficTypeIconReplaceLineName:
      type === "TÅG" || type === "GÅNG" || type === "BÅT",
  };
};

const mapPlace = (place: any): JourneyPlace | undefined => {
  if (!place) {
    return undefined;
  }

  const stopPointData = place.OgtStopPointData;

  return {
    Id: place.Id ?? place.PlaceId,
    PlaceName: place.PlaceName ?? "",
    OgtType: place.OgtType ?? "stop",

    ...(stopPointData
      ? {
          OgtStopPointData: {
            stopPointId: stopPointData.stopPointId ?? null,
            stopPointAlias: stopPointData.stopPointAlias ?? null,
            stopPointAliasId: stopPointData.stopPointAliasId ?? null,
          },
        }
      : {}),
  };
};

const formatDateReadable = (isoDate?: string) =>
  isoDate ? dayjs(isoDate).format("dddd D MMMM") : "";

const formatTimeRange = (start?: string, end?: string) => {
  if (!start || !end) {
    return "";
  }

  return `${dayjs(start).format("HH:mm")} - ${dayjs(end).format("HH:mm")}`;
};

const mapPartialJourney = (
  journey: any
): TravelPlanDetails["selectedJourney"] => {
  const sourceLinks = Array.isArray(journey?.RouteLinks)
    ? journey.RouteLinks
    : [];

  const routeLinks: RouteLink[] = sourceLinks.map(
    (link: any, index: number) => {
      const sourceLine = link.Line ?? link;
      const type = inferLineType(link);

      const groupKey =
        link.RouteLinkGroupType ??
        sourceLine?.Ogt?.OgtTrafficTypeGroupKey ??
        "";

      const lineName = sourceLine.LineName ?? "";

      const arrivalDateTime =
        link.ArrDateTime ??
        (index === sourceLinks.length - 1
          ? journey.ArrivalDateOfJourney
          : undefined);

      return {
        RouteLinkKey: link.RouteLinkKey ?? "",
        DepDateTime: link.DepDateTime,
        ArrDateTime: arrivalDateTime,
        From: mapPlace(link.From),
        To: mapPlace(link.To),

        Line: {
          LineName: lineName,
          LineNrReal: sourceLine.LineNrReal,
          LineTypeName: type,
          RunNo: sourceLine.RunNo,
          TrainNo: sourceLine.TrainNo,
          OperatorId: sourceLine.OperatorId,
          IsWalk: sourceLine.IsWalk ?? type === "GÅNG",

          Ogt: getOgtTrafficData(type, lineName, groupKey),
        },
      };
    }
  );

  const realConnections = routeLinks.filter((link) => !link.Line.IsWalk);

  return {
    fromName: journey?.From?.PlaceName ?? "",
    toName: journey?.To?.PlaceName ?? "",
    date: formatDateReadable(journey?.DateOfJourney),
    unformattedDate: journey?.DateOfJourney ?? "",
    unformattedArrival: journey?.ArrivalDateOfJourney ?? "",

    travelTime: formatTimeRange(
      journey?.DateOfJourney,
      journey?.ArrivalDateOfJourney
    ),

    journeyKey: journey?.JourneyKey ?? "",

    switches: Math.max(realConnections.length - 1, 0),

    line: routeLinks,
    UsedStartPlace: mapPlace(journey?.From),
    UsedEndPlace: mapPlace(journey?.To),
  };
};

export function mapApiDataToTravelPlanDetails(apiData: any): TravelPlanDetails {
  const partialJourneys = Array.isArray(apiData?.PartialJourneys)
    ? apiData.PartialJourneys
    : [];

  const [firstJourney, secondJourney] = partialJourneys;

  return {
    ReferenceId: apiData?.ReferenceId ?? "",
    EditId: apiData?.EditId ?? "",
    email: apiData?.Email ?? "",
    selectedJourney: mapPartialJourney(firstJourney),

    selectedReturnJourney: secondJourney
      ? mapPartialJourney(secondJourney)
      : undefined,
  };
}
